import { Spin } from '@/animation';
import { comma } from '@/utils/format.util';
import { FlexWrap } from '@repo/components';
import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ICON_IMAGE_EMPTY } from '@repo/assets';
import {
  convertComponentToImage,
  dataURItoBlob,
  getProductType,
} from '@repo/utils';
import { useMutation } from 'react-query';
import { fetchExcelData, uploadExel } from '@/api';
import {
  IExcelBody,
  IFormattedExcel,
  IUploadExcelImagePayload,
} from '@/types/common.type';
import { AxiosError } from 'axios';
import ExcelTable from '@/components/MedicalReceipt/ExcelTable';
import { ICON_REFRESH_GRAY } from '@repo/assets';
import { ICON_MAXIMIZE } from '@repo/assets';
import { IGlobalState } from '@/store';
import { useAppSelector } from '@/hooks/useReduxHooks';
import DetailBillModal from '@/components/MedicalAirport/DetailBillModal';
import { MEDICAL_AIRPORT_ITEM } from '@repo/types';

type Props = {
  hospitalName: string;
  refundIndex: number;
  supplyDate: string;
  price: number;
  refund: number;
  receiptPath: string;
  touristIndex: number;
  mainProductType: number;
  billItem?: MEDICAL_AIRPORT_ITEM;
};

function ExcelReceiptImage({
  hospitalName,
  refundIndex,
  supplyDate,
  price,
  refund,
  receiptPath,
  touristIndex,
  mainProductType,
  billItem,
}: Props) {
  const { nationality } = useAppSelector((state) => state) as IGlobalState;
  const excelRef = useRef<HTMLDivElement | null>(null);
  const [isPreviewModal, setIsPreviewModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(receiptPath);

  const nationIndex = nationality === 'KOR' ? 1 : 0;
  const text = {
    serviceType: ['Types of Medical Service', '시술내역'],
    purchaseDate: ['Date of Purchase', '시술일자'],
    payment: ['Payment', '결제금액'],
    refundAmount: ['Amount of Refund', '환급금'],
    refreshImage: ['Refresh image', '이미지 새로고침'],
  };

  const priceInfo = [
    [text.serviceType[nationIndex], getProductType(mainProductType)],
    [
      text.purchaseDate[nationIndex],
      `${format(new Date(supplyDate), 'yyyy.MM.dd')}`,
    ],
    [text.payment[nationIndex], `₩ ` + comma(price)],
    [text.refundAmount[nationIndex], `₩ ` + comma(refund)],
  ];

  const onTogglePreview = () => {
    setIsPreviewModal((prev) => !prev);
  };

  const { data, mutate: fetchMutate } = useMutation<
    IFormattedExcel,
    AxiosError,
    IExcelBody
  >('excelData', (payload) => fetchExcelData(payload), {
    retry: false,
    onError: () => {
      alert(`이미지를 불러오는데 실패하였습니다.\n잠시 후 다시 시도해주세요`);
      setIsLoading(false);
    },
    onSuccess: () => {
      setTimeout(uploadExcelImage, 500);
    },
  });

  const uploadExcelImage = async () => {
    if (excelRef) {
      const dataUrl = await convertComponentToImage(
        excelRef.current as HTMLElement,
      );
      const formData = new FormData();
      formData.append('file', dataURItoBlob(dataUrl));
      formData.append('refundId', refundIndex.toString());
      uploadMutate({ formData });
    }
  };

  const { mutate: uploadMutate } = useMutation<
    string,
    AxiosError,
    IUploadExcelImagePayload
  >((payload) => uploadExel(payload), {
    retry: false,
    onError: () => {
      alert(`이미지를 불러오는데 실패하였습니다.\n잠시 후 다시 시도해주세요`);
      setIsLoading(false);
    },
    onSuccess: (s3Url) => {
      setImageUrl(s3Url);
      setIsLoading(false);
    },
  });

  const makeReceiptFile = () => {
    setIsLoading(true);
    fetchMutate({
      touristIndex: Number(touristIndex),
      refundIndex: Number(refundIndex),
    });
  };

  useEffect(() => {
    if (!imageUrl) {
      makeReceiptFile();
    }
  }, []);

  return (
    <>
      <Container dir='column'>
        <Title>{hospitalName}</Title>
        <ContentContainer>
          <PriceItem dir='column' justifyContent='center'>
            {priceInfo.map(([key, value]) => (
              <PriceItemText key={key} dir='column'>
                <PriceLabel>{key}</PriceLabel>
                <PriceValue>{value}</PriceValue>
              </PriceItemText>
            ))}
          </PriceItem>
          {isLoading && (
            <NoImageContainer
              dir='column'
              justifyContent='center'
              alignItems='center'
            >
              <LoadingSpinner />
            </NoImageContainer>
          )}
          {!isLoading && imageUrl && (
            <ImageSection>
              <ImageSizeButton>
                <img src={ICON_MAXIMIZE} alt='' width={24} />
              </ImageSizeButton>
              <ProductImage
                src={imageUrl}
                onClick={onTogglePreview}
              />
              <RefreshButton onClick={makeReceiptFile}>
                <img src={ICON_REFRESH_GRAY} alt='' width={20} />
                {text.refreshImage[nationIndex]}
              </RefreshButton>
            </ImageSection>
          )}
          {!isLoading && !imageUrl && (
            <NoImageContainer
              dir='column'
              justifyContent='center'
              alignItems='center'
            >
              <NoImage src={ICON_IMAGE_EMPTY} />
              <NoImageText>No image</NoImageText>
            </NoImageContainer>
          )}
        </ContentContainer>
      </Container>
      {imageUrl && isPreviewModal && (
        <DetailBillModal
          billList={billItem ? [billItem] : []}
          openedId={refundIndex}
          onClose={() => setIsPreviewModal(false)}
        />
      )}
      {data && (
        <Hidden>
          <ExcelContainer ref={excelRef}>
            <ExcelTable data={data} />
          </ExcelContainer>
        </Hidden>
      )}
    </>
  );
}

const Container = styled(FlexWrap)`
  padding: 30px 0;
  border-bottom: 1px solid #cbccce;
  width: 100%;
  :last-child {
    margin-bottom: 24px;
  }
`;

const ContentContainer = styled(FlexWrap)``;
const PriceItem = styled(FlexWrap)`
  gap: 8px;
  flex: 1;
`;
const PriceItemText = styled(FlexWrap)``;
const Title = styled.h3`
  color: #030303;
  font-size: 18px;
  font-weight: 600;
  line-height: 27px;
  margin-bottom: 8px;
`;
const PriceLabel = styled.span`
  color: #80848a;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
`;
const PriceValue = styled.span`
  color: #030303;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
`;
const NoImageContainer = styled(FlexWrap)`
  background-color: #ececec;
  border: 1px solid #9fa1a7;
  border-radius: 4px;
  width: 152px;
  height: 126px;
`;
const NoImage = styled.img`
  width: 56px;
  height: 56px;
  margin-bottom: 8px;
`;
const NoImageText = styled.span`
  font-size: 11px;
  color: #9fa1a7;
  text-align: center;
  line-height: 20px;
`;
const ProductImage = styled.img`
  width: 154px;
  height: 126px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #9fa1a7;
  :hover {
    opacity: 70%;
  }
  transition: all 300ms ease-in;
`;
const LoadingSpinner = styled.div`
  animation: ${Spin} 700ms linear infinite;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  border: 4px solid #9fa1a7;
  border-top: 4px solid #ececec;
  margin: 12px 0;
`;
const Hidden = styled.div`
  opacity: 0;
`;
const ExcelContainer = styled.div<{ ref: any }>`
  width: 1200px;
  position: absolute;
  top: -2500px;
  left: 400px;
`;

const ImageSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-items: flex-start;
  gap: 16px;
`;

const RefreshButton = styled.button`
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 4px;
  background: #f4f4f4;
  color: #333;
  font-size: 13px;
  font-weight: 500;
`;

const ImageSizeButton = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
`;

export default ExcelReceiptImage;
